/**
 * Loading the SFX sprite (DESIGN-client §8, §9 "Bundles").
 *
 * `tools/audio/pack-sprites.ts` writes three files into `public/assets/audio/`: `sfx.webm`,
 * `sfx.mp3` and `sfx.json` (`{ id: [startSec, durSec] }`). Nothing loaded them. Every id in
 * `sfxMap.ts` therefore resolved to no sample and `AudioEngine.play` returned `audible:false`
 * for the entire game — the sprite shipped in the build, 400 kB of sound nobody could hear.
 *
 * Staged with the `table` bundle rather than `boot`: not one byte of it is needed before a player
 * reaches a table, and boot is what the progress bar measures.
 *
 * Never rejects. A missing or undecodable sprite costs the sound and nothing else, which is the
 * same contract `loadBundle` keeps for atlases.
 */
import { assetBase } from '../scene/assets/base';
import { getAudioEngine, type AudioEngine, type SpriteMap } from './AudioEngine';

export const SFX_SPRITE = 'sfx';

let pending: Promise<boolean> | null = null;

export interface SfxSpriteOptions {
  engine?: AudioEngine;
  base?: string;
  fetchFn?: typeof fetch;
}

/**
 * Fetch `sfx.json`, then hand the engine the webm/mp3 pair to decode against it. Resolves true
 * once the ids are registered. Memoised: the table scene installs audio on every mount.
 */
export function loadSfxSprite(opts: SfxSpriteOptions = {}): Promise<boolean> {
  pending ??= (async () => {
    const base = `${opts.base ?? assetBase()}/audio`;
    const engine = opts.engine ?? getAudioEngine();
    const fetchFn = opts.fetchFn ?? fetch;
    try {
      const res = await fetchFn(`${base}/${SFX_SPRITE}.json`);
      if (!res.ok) throw new Error(`sfx manifest ${res.status}`);
      const map = (await res.json()) as SpriteMap;
      await engine.loadSprite(SFX_SPRITE, { webm: `${base}/${SFX_SPRITE}.webm`, mp3: `${base}/${SFX_SPRITE}.mp3` }, map);
      return true;
    } catch (err) {
      console.warn('[audio] sfx sprite failed to load', err);
      return false;
    }
  })();
  return pending;
}

/** Test seam: forget the memoised load. */
export function resetSfxSprite(): void {
  pending = null;
}
